import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "../lib/api";
import { API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, ChevronLeft, ChevronRight, ExternalLink, Wrench, CreditCard } from "lucide-react";

// Calcula el estado efectivo de una organización considerando plan_override + Stripe
function getEffectiveInfo(org) {
  const override = org.plan_override;
  const expiryStr = org.plan_override_expires_at;
  const now = new Date();

  if (override) {
    if (expiryStr) {
      const expiry = new Date(expiryStr);
      if (expiry > now) {
        return { label: "Manual activo", badgeClass: "bg-emerald-100 text-emerald-700", type: "manual", expiry };
      } else {
        return { label: "Manual expirado", badgeClass: "bg-red-100 text-red-700", type: "manual_expired", expiry };
      }
    }
    return { label: "Manual activo", badgeClass: "bg-emerald-100 text-emerald-700", type: "manual", expiry: null };
  }

  const stripeStatus = org.subscription_status || "none";
  const stripeMap = {
    active:   { label: "Stripe activo",  badgeClass: "bg-blue-100 text-blue-700", type: "stripe" },
    past_due: { label: "Pago pendiente", badgeClass: "bg-amber-100 text-amber-700", type: "stripe" },
    canceled: { label: "Cancelado",      badgeClass: "bg-red-100 text-red-700", type: "stripe" },
    trialing: { label: "Trial",          badgeClass: "bg-purple-100 text-purple-700", type: "stripe" },
    none:     { label: "Sin plan",       badgeClass: "bg-slate-100 text-slate-500", type: "none" },
  };
  return { ...(stripeMap[stripeStatus] || stripeMap.none), expiry: null };
}

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ page, limit: 15 });
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      const res = await axios.get(`${API}/admin/organizations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgs(res.data.organizations);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrgs();
  };

  return (
    <div className="space-y-4" data-testid="admin-organizations">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Organizaciones</h1>
        <span className="text-sm text-slate-500">{total} total</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px] max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="Buscar por nombre o ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-org-search"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">Buscar</Button>
        </form>
        <select
          className="border border-slate-200 rounded-md px-3 py-1.5 text-sm"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          data-testid="admin-org-status-filter"
        >
          <option value="">Todos los estados</option>
          <option value="active">Stripe activo</option>
          <option value="past_due">Pago pendiente</option>
          <option value="canceled">Cancelado</option>
          <option value="trialing">Trial</option>
          <option value="manual">Manual activo</option>
          <option value="none">Sin plan</option>
        </select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-org-table">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Organización</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Owner</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Plan</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Estado</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Tipo</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Tienda</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-500">Creado</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-500"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-400">Cargando...</td></tr>
                ) : orgs.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-8 text-slate-400">No se encontraron organizaciones</td></tr>
                ) : orgs.map((org) => {
                  const info = getEffectiveInfo(org);
                  return (
                  <tr key={org.organization_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`admin-org-row-${org.organization_id}`}>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{org.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{org.organization_id}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{org.owner?.email || "-"}</td>
                    <td className="px-4 py-3">
                      <span className="capitalize text-slate-700 font-medium">{org.plan_key || "-"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium w-fit ${info.badgeClass}`}>
                          {info.label}
                        </span>
                        {info.expiry && (
                          <span className="text-xs text-slate-400">
                            Expira: {new Date(info.expiry).toLocaleDateString("es-ES")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {info.type === "manual" || info.type === "manual_expired" ? (
                        <span className="inline-flex items-center gap-1 text-xs text-orange-600 font-medium">
                          <Wrench className="w-3 h-3" /> Manual
                        </span>
                      ) : info.type === "stripe" ? (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600 font-medium">
                          <CreditCard className="w-3 h-3" /> Stripe
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{org.connected_shop || "-"}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {org.created_at ? new Date(org.created_at).toLocaleDateString("es-ES") : "-"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/organizations/${org.organization_id}`}
                        className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 text-xs"
                        data-testid={`admin-org-detail-${org.organization_id}`}
                      >
                        Ver <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Página {page} de {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
